import { useQuery } from '@tanstack/react-query'
import type {
  CampaignAudience,
  NotificationChannel,
  NotificationEvent,
  NotificationProviderDriver,
  NotificationProviderField,
  NotificationStatus,
  NotificationTemplate,
  NotificationVariable,
} from '@concours/shared'
import { useAuth } from '@concours/ui'

/** Notifications (phase 18) : modèles, fournisseurs, historique, envois groupés. */
export const NOTIFICATIONS_KEY = ['admin', 'notifications'] as const

export interface TemplateEntry {
  event: NotificationEvent
  label: string
  description: string
  variables: NotificationVariable[]
  portalVariables?: NotificationVariable[]
  sms: boolean
  template: NotificationTemplate
  default: NotificationTemplate
  customized: boolean
  version: number
  updatedAt: string | null
  updatedBy: string | null
}

export interface TemplatesResponse {
  data: TemplateEntry[]
  variables: Record<NotificationVariable, string>
  sample: Record<NotificationVariable, string>
}

export function useTemplates() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'templates'],
    queryFn: () => session.api.get<TemplatesResponse>('/admin/notifications/templates'),
  })
}

export interface Provider {
  id: string
  driver: NotificationProviderDriver
  driverLabel: string
  kind: 'email' | 'sms'
  label: string
  config: Record<string, string>
  secretsSet: string[]
  isActive: boolean
  missing: string[]
  lastTest: { at: string | null; ok: boolean; message: string | null } | null
  updatedAt: string | null
}

export interface ProvidersResponse {
  data: Provider[]
  drivers: Record<
    NotificationProviderDriver,
    {
      kind: 'email' | 'sms'
      label: string
      description: string
      fields: NotificationProviderField[]
    }
  >
}

export function useProviders() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'providers'],
    queryFn: () => session.api.get<ProvidersResponse>('/admin/notifications/providers'),
  })
}

export interface HistoryRow {
  id: string
  event: string
  eventLabel: string
  channel: NotificationChannel
  channelLabel: string
  recipient: string | null
  title: string
  status: NotificationStatus
  statusLabel: string
  attempts: number
  lastError: string | null
  provider: string | null
  competition: string | null
  read: boolean
  sentAt: string | null
  createdAt: string | null
}

export interface HistoryFilters {
  channel?: string
  status?: string
  event?: string
  page: number
}

export function useHistory(filters: HistoryFilters) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'history', filters],
    queryFn: () =>
      session.api.get<{
        data: HistoryRow[]
        meta: { page: number; perPage: number; total: number }
        counts: Partial<Record<NotificationStatus, number>>
      }>('/admin/notifications', {
        query: { ...filters, perPage: 25 },
      }),
  })
}

export interface Campaign {
  id: string
  audience: CampaignAudience
  audienceLabel: string
  stage: string | null
  channels: string[]
  title: string
  body: string
  status: 'queued' | 'running' | 'done' | 'failed'
  total: number
  done: number
  error: string | null
  requestedBy: string | null
  createdAt: string | null
  finishedAt: string | null
}

export function useCampaigns(competitionId: string) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'campaigns', competitionId],
    queryFn: () =>
      session.api.get<{ data: Campaign[]; audiences: Record<CampaignAudience, string> }>(
        `/admin/competitions/${competitionId}/notification-campaigns`
      ),
    enabled: Boolean(competitionId),
    refetchInterval: (q) =>
      q.state.data?.data.some((c) => c.status === 'queued' || c.status === 'running')
        ? 2000
        : false,
  })
}

export function useAudience(competitionId: string, audience: CampaignAudience, stageId: string) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'audience', competitionId, audience, stageId],
    queryFn: () =>
      session.api
        .get<{ recipients: number }>(`/admin/competitions/${competitionId}/notification-audience`, {
          query: { audience, ...(audience === 'stage' && stageId ? { stageId } : {}) },
        })
        .then((r) => r.recipients),
    enabled: Boolean(competitionId) && (audience !== 'stage' || Boolean(stageId)),
  })
}

export const STATUS_VARIANT = {
  pending: 'info',
  sent: 'success',
  failed: 'danger',
  skipped: 'neutral',
} as const
